import axios from "axios";

class NotificationService {
    constructor() {
        this.ws = null;
        this.reconnectTimer = null;
        this.reconnectCount = 0;
        this.maxReconnectCount = 5;
        this.messageCallback = null;
        this.connectionCallback = null;
        this.userType = null;
        this.userId = null;
    }

    init(userType, userId, onMessage, onConnectionChange) {
        this.userType = userType;
        this.userId = userId;
        this.messageCallback = onMessage;
        this.connectionCallback = onConnectionChange;
        this.connect(userType, userId);
    }

    connect(userType, userId) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }

        // WebSocket连接地址：开发环境直连8080，生产环境通过nginx代理
        const wsUrl = process.env.NODE_ENV === 'development'
            ? `ws://localhost:8080/ws/${userId}`
            : `/ws/${userId}`;

        try {
            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                console.log("WebSocket连接成功:", wsUrl);
                this.reconnectCount = 0;
                if (this.connectionCallback) {
                    this.connectionCallback(true);
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log("收到WebSocket消息:", data);
                    // 转换后端数据格式为前端格式
                    const notifications = Array.isArray(data) ? data : [data];
                    const formattedNotifications = notifications.map(item => this.formatNotification(item));
                    if (this.messageCallback) {
                        formattedNotifications.forEach(notification => {
                            this.messageCallback(notification);
                        });
                    }
                } catch (e) {
                    console.error("解析WebSocket消息失败:", e);
                }
            };

            this.ws.onclose = () => {
                console.log("WebSocket连接关闭");
                if (this.connectionCallback) {
                    this.connectionCallback(false);
                }
                this.reconnect(userType, userId);
            };

            this.ws.onerror = (error) => {
                console.error("WebSocket错误:", error);
                if (this.connectionCallback) {
                    this.connectionCallback(false);
                }
            };
        } catch (error) {
            console.error("创建WebSocket连接失败:", error);
            this.reconnect(userType, userId);
        }
    }

    // 转换后端数据格式为前端格式
    formatNotification(item) {
        const actionTypeMap = {
            1: { type: "like", action: "赞了你的课程", text: "点赞" },
            2: { type: "favorite", action: "收藏了你的课程", text: "收藏" },
            3: { type: "comment", action: "评论了你的课程", text: "评论" },
            4: { type: "reply", action: "回复了你的评论", text: "回复" }
        };

        const actionInfo = actionTypeMap[item.actionType] || { type: "unknown", action: "有新的互动", text: "消息" };

        return {
            id: String(item.id),
            userId: this.userId,
            type: actionInfo.type,
            actorName: item.senderName,
            actorUserType: item.senderUserType,
            action: actionInfo.action,
            actionText: actionInfo.text,
            lessonId: item.lessonId,
            lessonTitle: item.lessonName,
            commentId: item.commentId != null ? String(item.commentId) : null,
            commentType: item.commentType,
            rootId: item.rootId != null ? String(item.rootId) : null,
            firstCommentPage: item.firstCommentPage,
            createTime: item.createdAt,
            isRead: item.isRead === 1
        };
    }

    reconnect(userType, userId) {
        if (this.reconnectCount >= this.maxReconnectCount) {
            console.warn("WebSocket重连次数已达上限");
            return;
        }

        this.reconnectCount++;
        console.log(`WebSocket将在3秒后第${this.reconnectCount}次重连...`);
        this.reconnectTimer = setTimeout(() => {
            this.connect(userType, userId);
        }, 3000);
    }

    close() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }

    // 标记全部已读
    markAllAsRead(userType) {
        return new Promise((resolve, reject) => {
            axios.post('/api/message/read/all', {}, {
                headers: {
                    Authorization: localStorage.getItem("jwt-" + userType)
                }
            }).then((res) => {
                if (res.data.code === "200") {
                    resolve(res.data);
                } else {
                    reject(new Error(res.data.message));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // 清空全部通知
    clearAll(userType) {
        return new Promise((resolve, reject) => {
            axios.delete('/api/message/delete/all', {
                headers: {
                    Authorization: localStorage.getItem("jwt-" + userType)
                }
            }).then((res) => {
                if (res.data.code === "200") {
                    resolve(res.data);
                } else {
                    reject(new Error(res.data.message));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // 标记单条已读
    markAsRead(userType, notificationId) {
        return new Promise((resolve, reject) => {
            axios.post('/api/message/read', {
                id: parseInt(notificationId)
            }, {
                headers: {
                    Authorization: localStorage.getItem("jwt-" + userType)
                }
            }).then((res) => {
                if (res.data.code === "200") {
                    resolve(res.data);
                } else {
                    reject(new Error(res.data.message));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }
}

export default new NotificationService();